import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Download, ShieldCheck, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Admin → IP Acceptance Log
 * Append-only audit trail of every "Accept & Continue" click on the
 * Intellectual-Property notice modal. Supports text search (user email/name/IP),
 * date-range filtering (by acceptance timestamp), and CSV export of the
 * currently visible rows.
 */
export default function IpAcceptanceLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [limit, setLimit] = useState(200);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/ip-notice/acceptances`, {
        params: { limit },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load acceptance log');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const hay = `${r.user_email || ''} ${r.user_name || ''} ${r.source_ip || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateFrom || dateTo) {
        if (!r.accepted_at) return false;
        const d = parseISO(r.accepted_at);
        if (dateFrom && d < startOfDay(parseISO(dateFrom))) return false;
        if (dateTo && d > endOfDay(parseISO(dateTo))) return false;
      }
      return true;
    });
  }, [rows, searchQuery, dateFrom, dateTo]);

  const downloadCsv = () => {
    if (filtered.length === 0) {
      toast.error('Nothing to export');
      return;
    }
    const header = ['Accepted At', 'User Name', 'User Email', 'Source IP', 'User Agent'];
    const escape = (v) => {
      const s = (v ?? '').toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [header.join(',')];
    filtered.forEach((r) => {
      lines.push([
        r.accepted_at || '',
        r.user_name || '',
        r.user_email || '',
        r.source_ip || '',
        r.user_agent || '',
      ].map(escape).join(','));
    });
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ip-acceptance-log-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} record${filtered.length === 1 ? '' : 's'}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <Card data-testid="ip-acceptance-log-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            IP Acceptance Log
          </CardTitle>
          <CardDescription>
            Append-only audit trail of every user who accepted the Intellectual
            Property notice. Useful for legal review.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRows}
            disabled={loading}
            data-testid="ip-log-refresh-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={downloadCsv}
            disabled={filtered.length === 0}
            data-testid="ip-log-csv-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px] space-y-1.5">
            <Label className="text-xs text-slate-500">Search (name, email, IP)</Label>
            <Input
              type="search"
              placeholder="e.g. acme.com.au or 34.170."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="ip-log-search"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 w-36"
              data-testid="ip-log-date-from"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 w-36"
              data-testid="ip-log-date-to"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Fetch latest</Label>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm dark:bg-slate-900 dark:border-slate-700"
              data-testid="ip-log-limit"
            >
              <option value={50}>50</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>
          {(searchQuery || dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-9 text-slate-500"
              data-testid="ip-log-clear-filters"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Summary */}
        <div className="text-xs text-slate-500" data-testid="ip-log-summary">
          Showing <strong className="text-slate-700 dark:text-slate-200">{filtered.length}</strong>{' '}
          of {rows.length} record{rows.length === 1 ? '' : 's'} (latest {limit}).
        </div>

        {/* Table */}
        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded p-3" data-testid="ip-log-error">
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <ShieldCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No acceptance records match your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
            <Table data-testid="ip-log-table">
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-800/40">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Accepted At</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">User</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Source IP</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">User Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`ip-log-row-${r.id}`}>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {r.accepted_at ? format(parseISO(r.accepted_at), 'dd MMM yyyy, HH:mm:ss') : '—'}
                    </TableCell>
                    <TableCell className="text-sm">{r.user_name || '—'}</TableCell>
                    <TableCell className="text-sm font-mono text-slate-600 dark:text-slate-300">{r.user_email || '—'}</TableCell>
                    <TableCell className="text-sm font-mono">{r.source_ip || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-md truncate" title={r.user_agent}>
                      {r.user_agent || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
